import React, { useState, useEffect } from "react";
import { Table, message, Card, Button, Dropdown, Tooltip } from "antd";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Heading from "../../components/Heading";
import urls from "../../api/urls";
import { commonSorterForTable } from "../../utils";
import "./style.scss";
import API from "../../api";
const DeliveryPartnerList = () => {
  const [loading, setLoading] = useState(false);
  const [deliveryPartners, setDeliveryPartners] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();
  const { DP_LIST } = urls;

  // Handle back navigation to always go to Onboard Settings
  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      navigate("/onboarding-settings", { replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // Table columns configuration
  // helper: build a readable address safely
  const formatAddress = (r) => {
    const line1 = r?.addressLine1?.trim();
    const cityState = [r?.city, r?.state].filter(Boolean).join(", ");
    const pin = r?.pincode ? String(r.pincode).trim() : "";

    // "Line 1, City, State - Pincode" (handles missing pieces)
    return [
      line1,
      [cityState, pin && `- ${pin}`].filter(Boolean).join(" ")
    ].filter(Boolean).join(", ");
  };

  const columns = [
    {
      title: "Serial No.",
      key: "serialNo",
      width: 100,
      render: (_, __, index) => {
        const { current, pageSize } = pagination;
        return (current - 1) * pageSize + index + 1;
      },
    },
    {
      title: "Delivery Partner Name",
      dataIndex: "dpName",
      key: "dpName",
      ellipsis: true,
      sorter: (a, b) => commonSorterForTable(a, b, "dpName"),
    },
    {
      title: "Official Email",
      dataIndex: "officialEmail",
      key: "officialEmail",
      ellipsis: true,
      sorter: (a, b) => commonSorterForTable(a, b, "officialEmail"),
    },
    {
      title: "Official Contact",
      dataIndex: "officialContact",
      key: "officialContact",
      ellipsis: true,
      sorter: (a, b) => commonSorterForTable(a, b, "officialContact"),
    },

    // ✅ Merged Address column (replaces Address/City/State/Pincode)
    {
      title: "Address",
      key: "address",
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <Tooltip placement="topLeft" title={formatAddress(record)}>
          <span>{formatAddress(record)}</span>
        </Tooltip>
      ),
      sorter: (a, b) => formatAddress(a).localeCompare(formatAddress(b)),
    },

    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      ellipsis: true,
      sorter: (a, b) => commonSorterForTable(a, b, "zone"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => {
        const menuItems = [
          { key: "edit", label: "Edit", onClick: () => handleEdit(record) }
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <Button type="text" onClick={(e) => e.stopPropagation()}>
              <MoreOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];


  // Fetch delivery partners data
  const fetchDeliveryPartners = async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const url = `${DP_LIST}?pageNo=${page}&pageSize=${size}`;
      console.log("API URL:", url);

      let apiResult = await API.Api(
        url
      ).getWithAllHeadersDP();

      console.log("Raw API Response:", apiResult);

      if (apiResult.status) {
        const result = apiResult.response.data;
        console.log("Parsed JSON Result:", result);

        if (result.statusCode === 200) {
          console.log("Data to set:", result.data);
          console.log("Total count:", result.totalCount);

          setDeliveryPartners(result.data || []);
          setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: size,
            total: result.totalCount || 0,
          }));
        } else {
          console.log("Status code not 200:", result.statusCode);
          message.error(result.message || "Failed to fetch delivery partners");
        }
      } else {
        console.log("API status false:", apiResult);
        throw new Error(apiResult.response?.statusText || "API call failed");
      }
    } catch (error) {
      console.error("Error fetching delivery partners:", error);
      console.log("Error details:", error.message, error.stack);
      message.error(`Failed to load delivery partners: ${error.message}`);
      setDeliveryPartners([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    fetchDeliveryPartners(paginationInfo.current, paginationInfo.pageSize);
  };

  // Handle edit action
  const handleEdit = (record) => {
    console.log("Edit delivery partner:", record);
    navigate("/db-onboarding", {
      state: {
        editMode: true,
        deliveryPartnerData: record
      }
    });
  };

  // Handle add new delivery partner
  const handleAddNew = () => {
    navigate("/db-onboarding");
  };

  // Load data on component mount
  useEffect(() => {
    fetchDeliveryPartners();
  }, []);

  return (
    <div className="master-main-container">
      <div className="delivery-partner-list-container">
        <Heading title="Delivery Partner List" navigateTo="/onboarding-settings" />

        <Card>
          <div className="table-header" style={{ justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={handleAddNew}
              className="add-button"
            >
              <PlusOutlined /> Add New Delivery Partner
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={deliveryPartners}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ["5", "10", "20", "50"],
            }}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ x: 800 }}
            className="delivery-partner-table"
          />
        </Card>
      </div>
    </div>
  );
};

export default DeliveryPartnerList;
